import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import '../styles/LetsMove.css';

/**
 * /letsmove
 *
 * A 4-slide tribute shown to mam_non members the moment screening is opened
 * with a pass/fail decision pinned to them. Slides 1-3 are shared. Slide 4
 * branches:
 *   - pass : welcomes them officially into the club.
 *   - fail : sends them off with a warm goodbye and is followed by an
 *            automatic logout.
 *
 * After acknowledgement the server clears the screening gate so the user
 * (if pass) can resume normal navigation.
 */
const LetsMove = ({ lang = {} }) => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null); // 'pass' | 'fail' | null
  const [hasDecision, setHasDecision] = useState(null); // null = loading
  const [slides, setSlides] = useState([]);
  const [step, setStep] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);

  const SHARED_SLIDES = [
    {
      title: lang.LETSMOVE_SLIDE_1_TITLE || 'Thank you for coming home to ISP',
      body: lang.LETSMOVE_SLIDE_1_BODY || 'We cherish every moment you spent walking beside the club — from your very first meeting to the countless times you pushed yourself further than you thought you could.'
    },
    {
      title: lang.LETSMOVE_SLIDE_2_TITLE || 'Thank you for the memories we will never forget',
      body: lang.LETSMOVE_SLIDE_2_BODY || 'Every smile, every late-night session, every small story you shared — each one is a piece of the mosaic that makes the ISP family who we are.'
    },
    {
      title: lang.LETSMOVE_SLIDE_3_TITLE || 'Together, we shared moments that truly mattered',
      body: lang.LETSMOVE_SLIDE_3_BODY || 'The mam-non journey was never easy — but you showed up, you tried, and you left a mark that is uniquely yours.'
    }
  ];

  useEffect(() => {
    if (!token) {
      setHasDecision(false);
      return;
    }
    fetch('/api/screening/my-decision', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setHasDecision(!!d.hasDecision);
        setDecision(d.result || null);
        setSlides(Array.isArray(d.slides) ? d.slides : []);
      })
      .catch(() => setHasDecision(false));
  }, [token]);

  // Auto-rotate the slideshow images while the user reads.
  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => {
      setSlideIdx(i => (i + 1) % slides.length);
    }, 3500);
    return () => clearInterval(t);
  }, [slides.length]);

  const finalSlide = decision === 'pass'
    ? {
        title: lang.LETSMOVE_PASS_TITLE || 'Welcome to ISP Club',
        body: lang.LETSMOVE_PASS_BODY || 'On the road ahead, let us build a home at ISP that is joyful, meaningful, and worth coming back to. Your story with ISP — it truly begins now.',
        accent: 'pass'
      }
    : {
        title: lang.LETSMOVE_FAIL_TITLE || 'Until we meet again',
        body: lang.LETSMOVE_FAIL_BODY || 'On this stretch of the road we have to part ways for now. May our paths cross again one day at the ISP family — in another form, another shape, still as bright as you are today.',
        accent: 'fail'
      };

  const textSlides = [...SHARED_SLIDES, finalSlide];

  const acknowledgeAndContinue = async () => {
    try {
      await fetch('/api/screening/acknowledge', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_) { /* ignore */ }
    if (decision === 'fail') {
      // Log them out and send to home with a brief delay so the slide can fade.
      setTimeout(() => { logout(); navigate('/home', { replace: true }); }, 400);
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const next = () => {
    if (step < textSlides.length - 1) setStep(step + 1);
    else acknowledgeAndContinue();
  };
  const prev = () => { if (step > 0) setStep(step - 1); };

  // No pending decision => bounce home.
  useEffect(() => {
    if (hasDecision === false) {
      const t = setTimeout(() => navigate('/home', { replace: true }), 400);
      return () => clearTimeout(t);
    }
  }, [hasDecision, navigate]);

  if (hasDecision !== true) {
    return (
      <div className="letsmove-root letsmove-loading">
        <p>{lang.LETSMOVE_LOADING || 'Loading...'}</p>
      </div>
    );
  }

  const current = textSlides[step];
  const isLast = step === textSlides.length - 1;
  const accent = current.accent || 'shared';

  return (
    <div className={`letsmove-root accent-${accent}`}>
      {slides.length > 0 && (
        <div className="letsmove-slideshow" aria-hidden="true">
          {slides.map((fn, i) => (
            <div
              key={fn}
              className={`letsmove-slideshow-img ${i === slideIdx ? 'active' : ''}`}
              style={{ backgroundImage: `url(/uploads/screening/${fn})` }}
            />
          ))}
          <div className="letsmove-slideshow-veil" />
        </div>
      )}

      <div className="letsmove-stage">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className="letsmove-slide"
            initial={{ opacity: 0, x: 80 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -80 }}
            transition={{ duration: 0.55, ease: [0.22, 0.85, 0.32, 1] }}
          >
            <span className="letsmove-step-indicator">{step + 1} / {textSlides.length}</span>
            <h1 className="letsmove-title">{current.title}</h1>
            <p className="letsmove-body">{current.body}</p>

              {isLast && (
              <motion.div
                className={`letsmove-stamp ${accent}`}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.45 }}
              >
                {accent === 'pass'
                  ? (lang.LETSMOVE_STAMP_PASS || 'WELCOME')
                  : (lang.LETSMOVE_STAMP_FAIL || 'SEE YOU AGAIN')}
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="letsmove-progress">
        {textSlides.map((_, i) => (
          <span key={i} className={`letsmove-dot ${i === step ? 'active' : ''} ${i < step ? 'past' : ''}`} />
        ))}
      </div>

      <div className="letsmove-controls">
        {step > 0 && (
          <button className="letsmove-btn ghost" onClick={prev}>{lang.LETSMOVE_BACK || '← Back'}</button>
        )}
        <button className="letsmove-btn primary" onClick={next}>
          {isLast
            ? (accent === 'pass'
                ? (lang.LETSMOVE_PASS_CTA || 'Begin the journey →')
                : (lang.LETSMOVE_FAIL_CTA || 'Farewell, ISP →'))
            : (lang.LETSMOVE_CONTINUE || 'Continue →')}
        </button>
      </div>
    </div>
  );
};

export default LetsMove;
